import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Car, FileText, PlaneLanding, PlaneTakeoff, Route, X } from 'lucide-react';
import {
  DeliveryWindowBadge,
  EstimatedDaysBadge,
} from '../components/delivery/DeliveryDateBadges';
import CarrierTrackingPanel, {
  type CustomerCarrierShipment,
} from '../components/delivery/CarrierTrackingPanel';
import CustomerLayout from '../components/layout/CustomerLayout';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { PaginatedListLoading } from '../components/ui/PaginatedListLoading';
import { formatOrderReference, orderIdMatchesSearch } from '../utils/formatOrderReference';
import { openPdfFromBase64Payload } from '../utils/openPdfFromBase64Payload';
import CheckoutPaymentForm from '../components/checkout/CheckoutPaymentForm';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
const PAGE_SIZE = 8;
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

type SalesOrderItem = {
  id: string;
  packageId?: string | null;
  packageNameSnapshot?: string | null;
  productNameSnapshot: string;
  unit: 'LB' | 'KG' | 'PACK' | string;
  qty: number;
  originalUnitPriceUsdSnapshot?: number | string;
  productDiscountPercentSnapshot?: number | string;
  unitPriceUsdSnapshot?: number | string;
  lineTotalUsd?: number | string;
  lineDiscountUsd?: number | string;
  stockReservations?: Array<{
    id: string;
    qtyLbs: number;
    productId: string;
    product?: {
      id: string;
      name: string;
      baseUnit?: string;
    } | null;
  }>;
  allocations?: Array<{
    id: string;
    qtyLbs: number;
    lot?: {
      id: string;
      productId?: string;
      product?: {
        id: string;
        name: string;
        baseUnit?: string;
      } | null;
    } | null;
  }>;
  package?: {
    id: string;
    items?: Array<{
      id: string;
      qty: number;
      unit: string;
      productId?: string;
      product?: {
        id: string;
        name: string;
        baseUnit?: string;
      } | null;
    }>;
  } | null;
};

type SalesOrderEvent = {
  id: string;
  type: string;
  fromStatus?: string | null;
  toStatus?: string | null;
  createdAt: string;
};

type SalesOrder = {
  id: string;
  status: string;
  currency: string;
  totalUsd: number;
  createdAt: string;
  estimatedDeliveryDays: number;
  items: SalesOrderItem[];
  events: SalesOrderEvent[];
  addressVerification?: {
    status?: 'PENDING' | 'VERIFIED' | 'FAILED';
    checkedAt?: string | null;
    error?: string | null;
  } | null;
  customer?: {
    customerProfile?: {
      drivingDurationSeconds?: number | null;
      operationalDurationSeconds?: number | null;
      drivingDistanceMeters?: number | null;
      flightDurationSeconds?: number | null;
      isAbroadDelivery?: boolean | null;
      abroadRoutePlan?: unknown | null;
      drivingComputedAt?: string | null;
      drivingRouteStatus?: string | null;
    } | null;
  } | null;
  deliveryBatch?: {
    id: string;
    name: string | null;
    areaLabel: string;
    windowStart: string;
    windowEnd: string;
    status: string;
  } | null;
  payment?: { id: string } | null;
  balancePayments?: Array<{ id: string }>;
  isPreorderOrder?: boolean;
  preorderDepositUsd?: number;
  preorderRemainingUsd?: number;
  fulfillmentMethod?: 'IN_HOUSE' | 'FEDEX' | 'UPS' | null;
  primaryTrackingNumber?: string | null;
  carrierShipments?: CustomerCarrierShipment[];
  preorderCancellationFeeUsd?: number;
  refundRequests?: Array<{
    id: string;
    reason: string;
    status: string;
    requestNote?: string | null;
    createdAt: string;
    evidences?: Array<{ id: string; imageUrl: string }>;
  }>;
};

const FLOW = ['PENDING', 'PAID', 'SHIPPED', 'DELIVERED', 'CLOSED'] as const;
const INCIDENT_STATUSES = new Set([
  'UNREACHABLE',
  'DAMAGED',
  'INVESTIGATING',
  'FAULT_EMPLOYEE',
  'FAULT_CUSTOMER',
  'RESHIP_PENDING',
  'RESHIPPED',
  'INSUFFICIENT_STOCK_PENDING',
]);

function formatDrivingEta(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
  return parts.join(' ');
}

function metersToMiles(meters: number): string {
  const miles = meters / 1609.344;
  return `${miles.toFixed(miles >= 10 ? 1 : 2)} mi`;
}

function carrierShipmentsForOrder(order: SalesOrder): CustomerCarrierShipment[] {
  if (order.carrierShipments?.length) return order.carrierShipments;
  if (
    order.primaryTrackingNumber &&
    order.fulfillmentMethod &&
    order.fulfillmentMethod !== 'IN_HOUSE'
  ) {
    return [
      {
        id: `${order.id}-tracking`,
        provider: order.fulfillmentMethod,
        fulfillmentMethod: order.fulfillmentMethod,
        status:
          order.status === 'DELIVERED' || order.status === 'CLOSED'
            ? 'DELIVERED'
            : 'IN_TRANSIT',
        masterTrackingNumber: order.primaryTrackingNumber,
        trackingEvents: [],
      },
    ];
  }
  return [];
}

function StatusTimeline({ status }: { status: string }) {
  const { t } = useTranslation();
  const label = (code: string) =>
    t(`account.order_status_${code}`, { defaultValue: code });
  const currentIdx = FLOW.indexOf(status as (typeof FLOW)[number]);
  const isCancelled = status === 'CANCELLED';
  const isRefunded = status === 'REFUNDED';
  const isIncident = INCIDENT_STATUSES.has(status);
  return (
    <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-white via-slate-50 to-white p-3 shadow-sm">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
        {t('account.order_status_label', { defaultValue: 'Order status' })}
      </div>
      <div className="overflow-x-auto pb-1">
        <div className="flex min-w-[19rem] items-center gap-1.5 sm:min-w-[28rem] sm:gap-2">
          {FLOW.map((s, idx) => {
            const done = !isCancelled && currentIdx >= idx;
            const active = !isCancelled && currentIdx === idx;
            return (
              <React.Fragment key={s}>
                <div
                  className={[
                    'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-1',
                    done
                      ? 'border-brand-200 bg-brand-50'
                      : active
                        ? 'border-brand-200 bg-brand-50/70'
                        : 'border-slate-200 bg-white',
                  ].join(' ')}
                >
                  <div
                    className={[
                      'h-2.5 w-2.5 rounded-full border',
                      done
                        ? 'border-brand-600 bg-brand-600'
                        : active
                          ? 'border-brand-600 bg-brand-100'
                          : 'border-slate-300 bg-white',
                    ].join(' ')}
                  />
                  <div
                    className={[
                      'text-[10px] font-bold uppercase tracking-wide whitespace-nowrap',
                      done || active ? 'text-slate-900' : 'text-slate-500',
                    ].join(' ')}
                  >
                    {label(s)}
                  </div>
                </div>
                {idx !== FLOW.length - 1 ? (
                  <div
                    className={[
                      'h-0.5 min-w-3 flex-1 rounded-full',
                      done ? 'bg-brand-500/80' : 'bg-slate-200',
                    ].join(' ')}
                  />
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      {isCancelled ? (
        <div className="mt-2 text-xs font-semibold text-rose-700">
          {label('CANCELLED')}
        </div>
      ) : null}
      {isRefunded ? (
        <div className="mt-2 text-xs font-semibold text-rose-700">
          {label('REFUNDED')}
        </div>
      ) : null}
      {isIncident ? (
        <div className="mt-2 text-xs font-semibold text-amber-700">
          {label(status)}
        </div>
      ) : null}
    </div>
  );
}

export default function AccountOrdersPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const formatStatus = (code: string) =>
    t(`account.order_status_${code}`, { defaultValue: code });
  const { isAuthenticated, authorizedFetch } = useAuth();
  const { formatPrice, formatMajorInCurrency } = useCurrency();

  const orderIdFromCheckout =
    typeof location.state === 'object' &&
    location.state !== null &&
    'orderId' in location.state &&
    typeof (location.state as { orderId?: unknown }).orderId === 'string'
      ? (location.state as { orderId: string }).orderId
      : undefined;

  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyOrderId, setBusyOrderId] = useState<string | null>(null);
  const [receiptBusyId, setReceiptBusyId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState(1);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Record<string, true>>(
    {},
  );
  const [expandedPackageRows, setExpandedPackageRows] = useState<Record<string, boolean>>({});
  const [expandedPricingRows, setExpandedPricingRows] = useState<Record<string, boolean>>({});
  const [refundModal, setRefundModal] = useState<{
    orderId: string;
    stage: 'PAID' | 'DELIVERED';
    reason: string;
    note: string;
    evidenceUrl: string;
    evidenceExplanation: string;
  } | null>(null);
  const stripePromise = useMemo(
    () => (STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null),
    [],
  );
  const [balancePayModal, setBalancePayModal] = useState<{
    orderId: string;
    clientSecret: string;
    dueFormatted: string;
  } | null>(null);
  const [balancePayBusyOrderId, setBalancePayBusyOrderId] = useState<string | null>(null);
  const [trackingRefreshBusyId, setTrackingRefreshBusyId] = useState<string | null>(null);

  const packageBreakdownByItem = React.useCallback((item: SalesOrderItem) => {
    const toLbs = (qty: number, unit?: string) => {
      const u = String(unit || '').toUpperCase();
      if (u === 'KG') return qty * 2.20462;
      return qty;
    };
    const byProduct = new Map<string, { name: string; qtyLbs: number }>();
    const reservationRows = item.stockReservations || [];
    if (reservationRows.length > 0) {
      for (const r of reservationRows) {
        const key = String(r.productId || r.product?.id || '');
        if (!key) continue;
        const prev = byProduct.get(key);
        byProduct.set(key, {
          name:
            r.product?.name ||
            t('account.item_fallback', { defaultValue: 'Item' }),
          qtyLbs: Number(r.qtyLbs || 0) + Number(prev?.qtyLbs || 0),
        });
      }
    } else {
      const allocRows = item.allocations || [];
      if (allocRows.length > 0) {
        for (const a of allocRows) {
          const key = String(a.lot?.productId || a.lot?.product?.id || '');
          if (!key) continue;
          const prev = byProduct.get(key);
          byProduct.set(key, {
            name:
              a.lot?.product?.name ||
              t('account.item_fallback', { defaultValue: 'Item' }),
            qtyLbs: Number(a.qtyLbs || 0) + Number(prev?.qtyLbs || 0),
          });
        }
      } else {
        const packageItems = item.package?.items || [];
        for (const p of packageItems) {
          const key = String(p.productId || p.product?.id || '');
          if (!key) continue;
          const prev = byProduct.get(key);
          byProduct.set(key, {
            name:
              p.product?.name ||
              t('account.item_fallback', { defaultValue: 'Item' }),
            qtyLbs:
              toLbs(Number(p.qty || 0), p.unit) * Number(item.qty || 0) +
              Number(prev?.qtyLbs || 0),
          });
        }
      }
    }
    return Array.from(byProduct.values()).map((row) => ({
      ...row,
      qtyPerBundleLbs:
        item.qty > 0 ? row.qtyLbs / Number(item.qty || 1) : row.qtyLbs,
    }));
  }, [t]);

  const load = React.useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/sales/orders/me`);
      if (!res.ok) throw new Error(t('account.orders_load_error'));
      const data = (await res.json()) as SalesOrder[];
      setOrders(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('account.orders_load_error'));
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, isAuthenticated, t]);

  const refreshCarrierTracking = React.useCallback(
    async (orderId: string) => {
      setTrackingRefreshBusyId(orderId);
      try {
        const res = await authorizedFetch(
          `${API_BASE}/sales/orders/${orderId}/carrier-tracking/refresh`,
          { method: 'POST' },
        );
        if (!res.ok) {
          throw new Error(
            t('account.carrier_tracking_refresh_error', {
              defaultValue: 'Could not refresh tracking.',
            }),
          );
        }
        const data = (await res.json()) as {
          status?: string;
          primaryTrackingNumber?: string | null;
          fulfillmentMethod?: SalesOrder['fulfillmentMethod'];
          carrierShipments?: CustomerCarrierShipment[];
        };
        setOrders((prev) =>
          prev.map((order) =>
            order.id === orderId
              ? {
                  ...order,
                  status: data.status ?? order.status,
                  primaryTrackingNumber:
                    data.primaryTrackingNumber ?? order.primaryTrackingNumber,
                  fulfillmentMethod:
                    data.fulfillmentMethod ?? order.fulfillmentMethod,
                  carrierShipments: data.carrierShipments ?? order.carrierShipments,
                }
              : order,
          ),
        );
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : t('account.carrier_tracking_refresh_error', {
                defaultValue: 'Could not refresh tracking.',
              }),
        );
      } finally {
        setTrackingRefreshBusyId(null);
      }
    },
    [authorizedFetch, t],
  );

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) return;
    void (async () => {
      if (orderIdFromCheckout) {
        try {
          await authorizedFetch(`${API_BASE}/payments/orders/${orderIdFromCheckout}/sync`, {
            method: 'POST',
          });
        } catch {
          /* webhook may have already updated; still load list */
        }
      }
      if (cancelled) return;
      await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, isAuthenticated, load, orderIdFromCheckout]);

  useEffect(() => {
    if (!orderIdFromCheckout) return;
    setExpandedOrderIds((prev) => ({ ...prev, [orderIdFromCheckout]: true }));
  }, [orderIdFromCheckout]);

  const cancelOrder = async (orderId: string, isPreorder?: boolean) => {
    try {
      setBusyOrderId(orderId);
      setError(null);
      const endpoint = isPreorder
        ? `${API_BASE}/sales/orders/${orderId}/preorder/cancel`
        : `${API_BASE}/sales/orders/${orderId}/cancel`;
      const res = await authorizedFetch(endpoint, {
        method: 'POST',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).message || t('account.orders_cancel_error'));
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('account.orders_cancel_error'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const downloadReceipt = async (orderId: string) => {
    try {
      setReceiptBusyId(orderId);
      setError(null);
      const res = await authorizedFetch(`${API_BASE}/sales/orders/${orderId}/receipt`);
      if (!res.ok) throw new Error(t('account.order_receipt_error'));
      const data = (await res.json()) as {
        ok?: boolean;
        filename: string;
        contentType: string;
        data: string;
      };
      if (!data?.data) throw new Error(t('account.order_receipt_error'));
      openPdfFromBase64Payload(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('account.order_receipt_error'));
    } finally {
      setReceiptBusyId(null);
    }
  };

  const requestRefund = async () => {
    if (!refundModal) return;
    try {
      const requiresEvidence = refundModal.stage === 'DELIVERED';
      if (requiresEvidence && !refundModal.evidenceUrl) {
      setError(
        t('account.refund_evidence_required', {
          defaultValue: 'Please upload evidence image for delivered-order refund requests.',
        }),
      );
        return;
      }
      const res = await authorizedFetch(
        `${API_BASE}/sales/orders/${refundModal.orderId}/refund/request`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reason: refundModal.reason,
            note: refundModal.note || undefined,
            evidenceUrl: refundModal.evidenceUrl || undefined,
            evidenceExplanation: refundModal.evidenceExplanation || undefined,
          }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          (body as any).message ||
            t('account.refund_request_error', {
              defaultValue: 'Unable to request refund',
            }),
        );
      }
      setRefundModal(null);
      await load();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('account.refund_request_error', {
              defaultValue: 'Unable to request refund',
            }),
      );
    }
  };

  const startPayRemainingBalance = async (order: SalesOrder) => {
    try {
      setBalancePayBusyOrderId(order.id);
      setError(null);
      if (!stripePromise) {
        throw new Error(
          t('storefront.checkout_stripe_no_pk', {
            defaultValue: 'Card payments are currently unavailable. Please try again later.',
          }),
        );
      }
      const cfgRes = await authorizedFetch(`${API_BASE}/payments/config`);
      const cfg = (await cfgRes.json().catch(() => ({}))) as { paymentsEnabled?: boolean };
      if (!cfg?.paymentsEnabled) {
        throw new Error(
          t('storefront.checkout_stripe_server_off', {
            defaultValue: 'Online payment is currently unavailable.',
          }),
        );
      }
      const res = await authorizedFetch(`${API_BASE}/payments/intents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId: order.id, purpose: 'BALANCE' }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || t('storefront.checkout_pay_prepare_error'));
      }
      const data = (await res.json()) as { clientSecret?: string | null; amountMinor?: number; currency?: string };
      const clientSecret = String(data.clientSecret || '').trim();
      if (!clientSecret) throw new Error(t('storefront.checkout_pay_prepare_error'));
      const dueFormatted = formatMajorInCurrency(
        Number((data.amountMinor ?? 0) / 100),
        String(data.currency || order.currency || 'USD').toUpperCase(),
      );
      setBalancePayModal({ orderId: order.id, clientSecret, dueFormatted });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('storefront.checkout_pay_prepare_error'));
    } finally {
      setBalancePayBusyOrderId(null);
    }
  };

  const handleBalancePaymentSuccess = async () => {
    if (!balancePayModal) return;
    try {
      await authorizedFetch(`${API_BASE}/payments/orders/${balancePayModal.orderId}/sync`, {
        method: 'POST',
      });
    } catch {
      // webhook may complete slightly later
    }
    setBalancePayModal(null);
    await load();
  };

  const statusCounts = useMemo(() => {
    return orders.reduce<Record<string, number>>((acc, o) => {
      acc[o.status] = (acc[o.status] || 0) + 1;
      return acc;
    }, {});
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return orders.filter((o) => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (!q) return true;
      const hasItemMatch = o.items.some((it) => it.productNameSnapshot.toLowerCase().includes(q));
      return orderIdMatchesSearch(o.id, q) || hasItemMatch;
    });
  }, [orders, searchQuery, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter]);

  return (
    <CustomerLayout>
      <div className="flex flex-col gap-8">
        <header>
          <h1 className="workspace-page-title">
            {t('account.orders_title')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('account.orders_subtitle')}
          </p>
        </header>

        {error && <p className="text-xs text-red-500">{error}</p>}

        {isAuthenticated && !loading && orders.length > 0 ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('account.total_orders', { defaultValue: 'Total orders' })}
                </p>
                <p className="text-lg font-bold text-slate-900">{orders.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('account.active_orders', { defaultValue: 'Active' })}
                </p>
                <p className="text-lg font-bold text-slate-900">
                  {
                    orders.filter(
                      (o) => !['CLOSED', 'CANCELLED', 'REFUNDED'].includes(o.status),
                    ).length
                  }
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('account.filtered_orders', { defaultValue: 'Filtered' })}
                </p>
                <p className="text-lg font-bold text-slate-900">{filteredOrders.length}</p>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('account.orders_search_placeholder')}
                className="w-full min-w-0 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70 sm:max-w-md"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
              >
                <option value="">
                  {t('account.all_statuses', {
                    defaultValue: `${t('common.all')} statuses`,
                  })}
                </option>
                {Object.keys(statusCounts).map((status) => (
                  <option key={status} value={status}>
                    {formatStatus(status)} ({statusCounts[status]})
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('');
                }}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
              >
                {t('common.clear')}
              </button>
            </div>
          </section>
        ) : null}

        <section className="max-w-5xl">
          {!isAuthenticated ? (
            <p className="text-sm text-slate-600">
              {t('account.sign_in_required')}
            </p>
          ) : loading ? (
            <PaginatedListLoading title={t('common.list_loading_title')} skeletonRows={3} />
          ) : orders.length === 0 ? (
            <p className="text-sm text-slate-600">{t('account.orders_empty')}</p>
          ) : filteredOrders.length === 0 ? (
            <p className="text-sm text-slate-600">{t('account.orders_empty_filtered')}</p>
          ) : (
            (() => {
              const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
              const safePage = Math.min(page, totalPages);
              const pagedOrders = filteredOrders.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
              return (
            <div className="space-y-4">
              {pagedOrders.map((o) => {
                const showOrderBody = !!expandedOrderIds[o.id];
                const carrierShipments = carrierShipmentsForOrder(o);
                const prof = o.customer?.customerProfile ?? null;
                const driveSeconds = prof?.operationalDurationSeconds ?? prof?.drivingDurationSeconds ?? null;
                const driveMeters = prof?.drivingDistanceMeters ?? null;
                const flightSeconds = prof?.flightDurationSeconds ?? null;
                const hasDrive = typeof driveSeconds === 'number' && driveSeconds > 0;
                const hasMeters = typeof driveMeters === 'number' && driveMeters > 0;
                const isAbroad = prof?.isAbroadDelivery === true;
                return (
                <div key={o.id} className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                    <div className="grid min-w-0 grid-cols-1 gap-3 min-[480px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] min-[480px]:items-start min-[480px]:gap-6">
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          {t('account.order_id')}
                        </div>
                        <div className="mt-1 font-mono text-[10px] leading-snug break-all text-slate-900">
                          {formatOrderReference(o.id)}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {new Date(o.createdAt).toLocaleString()}
                        </div>
                      </div>
                      <div className="min-w-0 min-[480px]:text-right">
                        {o.isPreorderOrder ? (
                          <>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-[480px]:text-right">
                              {t('account.preorder_payments_heading')}
                            </div>
                            <div className="mt-2 space-y-3 text-left min-[480px]:text-right">
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                  {t('account.preorder_deposit_payment_label')}
                                </div>
                                <div className="mt-0.5 font-mono text-[11px] leading-snug break-all text-slate-900">
                                  {o.payment?.id ?? (
                                    <span className="font-sans text-slate-500">
                                      {t('account.payment_id_none')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                  {(o.balancePayments?.length ?? 0) > 1
                                    ? t('account.preorder_balance_payments_label')
                                    : t('account.preorder_balance_payment_label')}
                                </div>
                                {(o.balancePayments?.length ?? 0) > 0 ? (
                                  <div className="mt-1 space-y-1.5">
                                    {(o.balancePayments ?? []).map((bp, i) => (
                                      <div
                                        key={bp.id}
                                        className="font-mono text-[11px] leading-snug break-all text-slate-900"
                                      >
                                        {(o.balancePayments?.length ?? 0) > 1 ? (
                                          <span className="mr-1.5 inline-block font-sans text-[10px] font-semibold text-slate-500">
                                            {t('account.preorder_balance_payment_index', { n: i + 1 })}
                                          </span>
                                        ) : null}
                                        {bp.id}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="mt-0.5 font-sans text-[11px] leading-snug text-slate-500">
                                    {t('account.preorder_balance_payment_pending')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              {t('account.payment_id')}
                            </div>
                            <div className="mt-1 font-mono text-[11px] leading-snug break-all text-slate-900">
                              {o.payment?.id ?? (
                                <span className="font-sans text-slate-500">
                                  {t('account.payment_id_none')}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="justify-self-start rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-right sm:justify-self-end sm:min-w-[7rem]">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        {t('account.total', { defaultValue: 'Total' })}
                      </div>
                      <div className="mt-0.5 text-sm font-semibold text-slate-900">
                        {formatPrice(Number(o.totalUsd))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex w-full flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void downloadReceipt(o.id)}
                        disabled={receiptBusyId === o.id}
                        title={t('account.order_receipt_download')}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400 disabled:opacity-50"
                      >
                        <FileText className="h-3.5 w-3.5" aria-hidden />
                        {receiptBusyId === o.id
                          ? t('common.loading', { defaultValue: '…' })
                          : t('account.order_receipt_download')}
                      </button>
                      <span className="rounded-full bg-slate-900/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        {formatStatus(o.status)}
                      </span>
                      {o.addressVerification?.status && o.addressVerification.status !== 'VERIFIED' ? (
                        <span
                          className={[
                            'rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide',
                            o.addressVerification.status === 'FAILED'
                              ? 'bg-rose-50 text-rose-700'
                              : 'bg-amber-50 text-amber-700',
                          ].join(' ')}
                        >
                          {t('account.address_status', {
                            defaultValue: 'Address {{status}}',
                            status: o.addressVerification.status.toLowerCase(),
                          })}
                        </span>
                      ) : null}
                      {o.refundRequests?.[0] ? (
                        <span className="rounded-full bg-amber-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                          {t('account.refund_status_badge', {
                            defaultValue: 'Refund {{status}}',
                            status: o.refundRequests[0].status,
                          })}
                        </span>
                      ) : null}
                      {o.isPreorderOrder ? (
                        <span className="rounded-full bg-indigo-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-800">
                          Pre-order
                        </span>
                      ) : null}
                  </div>
                  {o.isPreorderOrder ? (
                    <p className="mt-2 text-xs text-slate-600">
                      Deposit paid: {formatPrice(Number(o.preorderDepositUsd ?? 0))} · Remaining:{' '}
                      {formatPrice(Number(o.preorderRemainingUsd ?? 0))}
                    </p>
                  ) : null}
                  <div className="mt-2 text-xs text-slate-600">
                    {t('account.items_count_last_update', {
                      defaultValue: '{{count}} item{{suffix}} · Last update',
                      count: o.items.length,
                      suffix: o.items.length > 1 ? 's' : '',
                    })}{' '}
                    {new Date(
                      o.events.length
                        ? o.events[o.events.length - 1].createdAt
                        : o.createdAt,
                    ).toLocaleString()}
                  </div>
                  {carrierShipments.length > 0 ? (
                    <div className="mt-3">
                      <CarrierTrackingPanel
                        shipments={carrierShipments}
                        onRefresh={() => refreshCarrierTracking(o.id)}
                        refreshing={trackingRefreshBusyId === o.id}
                        compact
                      />
                    </div>
                  ) : null}
                  <div className="mt-3">
                    <StatusTimeline status={o.status} />
                  </div>
                  <div className="mt-2 flex flex-wrap justify-end gap-2">
                    {o.status === 'PREORDER_BALANCE_DUE' ? (
                      <button
                        type="button"
                        onClick={() => void startPayRemainingBalance(o)}
                        className="rounded-full border border-emerald-500 bg-emerald-500 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={balancePayBusyOrderId === o.id}
                      >
                        {balancePayBusyOrderId === o.id
                          ? t('common.loading', { defaultValue: 'Loading...' })
                          : t('account.pay_remaining_balance', {
                              defaultValue: 'Pay remaining balance',
                            })}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedOrderIds((prev) => {
                          if (prev[o.id]) {
                            const next = { ...prev };
                            delete next[o.id];
                            return next;
                          }
                          return { ...prev, [o.id]: true };
                        })
                      }
                      className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
                    >
                      {showOrderBody
                        ? t('account.hide_details', { defaultValue: 'Hide details' })
                        : t('account.view_details', { defaultValue: 'View details' })}
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <EstimatedDaysBadge>
                      {t('account.eta_days', { days: o.estimatedDeliveryDays })}
                    </EstimatedDaysBadge>
                    {hasDrive || hasMeters || isAbroad ? (
                      <span className="inline-flex max-w-full flex-wrap items-center gap-2 rounded-full bg-slate-900/5 px-3 py-1 text-[11px] font-semibold text-slate-700">
                        <span className="inline-flex items-center gap-1">
                          <Route className="h-3.5 w-3.5" />
                          {isAbroad
                            ? t('account.route_intermodal', {
                                defaultValue: 'Intermodal',
                              })
                            : t('account.route_road', { defaultValue: 'Road' })}
                        </span>
                        {hasDrive ? (
                          <span className="inline-flex items-center gap-1 text-slate-600">
                            <Car className="h-3.5 w-3.5" />
                            {formatDrivingEta(driveSeconds as number)}
                          </span>
                        ) : null}
                        {hasMeters ? (
                          <span className="text-slate-600">{metersToMiles(driveMeters as number)}</span>
                        ) : null}
                        {flightSeconds != null && flightSeconds > 0 ? (
                          <span className="inline-flex items-center gap-1 text-slate-600">
                            <PlaneTakeoff className="h-3.5 w-3.5" />
                            {formatDrivingEta(flightSeconds)}
                            <PlaneLanding className="h-3.5 w-3.5" />
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                    {o.deliveryBatch?.windowStart && o.deliveryBatch?.windowEnd ? (
                      <DeliveryWindowBadge
                        windowStart={o.deliveryBatch.windowStart}
                        windowEnd={o.deliveryBatch.windowEnd}
                        fromLabel={t('account.delivery_window_from')}
                        untilLabel={t('account.delivery_window_until')}
                        variant="compact"
                      />
                    ) : null}
                  </div>
                  {showOrderBody ? (
                    <>
                  {o.addressVerification?.status === 'FAILED' ? (
                    <p className="mt-3 text-xs text-rose-700">
                      {o.addressVerification.error ||
                        t('account.address_verification_failed', {
                          defaultValue:
                            'Address verification failed. Update your account address to unblock shipping.',
                        })}
                    </p>
                  ) : null}
                  <div className="mt-3 grid gap-2 text-xs text-slate-700">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('account.items')}
                    </div>
                    <div className="space-y-2">
                      {o.items.map((it) => {
                        const isPackage = it.unit === 'PACK' || Boolean(it.packageId);
                        const details = isPackage ? packageBreakdownByItem(it) : [];
                        const expandKey = `${o.id}:${it.id}`;
                        const expanded = Boolean(expandedPackageRows[expandKey]);
                        const pricingExpanded = Boolean(expandedPricingRows[expandKey]);
                        const unitPrice = Number(it.unitPriceUsdSnapshot || 0);
                        const listPrice = Number(
                          it.originalUnitPriceUsdSnapshot ||
                            it.unitPriceUsdSnapshot ||
                            0,
                        );
                        const discountPercent = Number(it.productDiscountPercentSnapshot || 0);
                        const lineDiscount = Number(it.lineDiscountUsd || 0);
                        return (
                          <div key={it.id} className="rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-3 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-bold text-slate-900">
                                  {it.productNameSnapshot}
                                </p>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  <span className={['rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide', isPackage ? 'bg-violet-100 text-violet-800' : 'bg-emerald-100 text-emerald-800'].join(' ')}>
                                    {isPackage
                                      ? t('account.item_type_package', {
                                          defaultValue: 'Package',
                                        })
                                      : t('account.item_type_product', {
                                          defaultValue: 'Product',
                                        })}
                                  </span>
                                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                                    Qty {it.qty} {it.unit}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  {t('account.line_total', { defaultValue: 'Line total' })}
                                </p>
                                <p className="text-sm font-extrabold text-slate-900">
                                  {formatPrice(Number(it.lineTotalUsd || 0))}
                                </p>
                              </div>
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                                {t('account.unit_price_short', {
                                  defaultValue: 'Unit {{price}}',
                                  price: formatPrice(unitPrice),
                                })}
                              </span>
                              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800">
                                {t('account.discount_short', {
                                  defaultValue: 'Discount {{percent}}%',
                                  percent: discountPercent.toFixed(0),
                                })}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedPricingRows((prev) => ({
                                    ...prev,
                                    [expandKey]: !prev[expandKey],
                                  }))
                                }
                                className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
                              >
                                {pricingExpanded
                                  ? t('account.hide_pricing_details', {
                                      defaultValue: 'Hide pricing details',
                                    })
                                  : t('account.view_pricing_details', {
                                      defaultValue: 'View pricing details',
                                    })}
                              </button>
                            </div>
                            {pricingExpanded ? (
                              <div className="mt-2 grid gap-1.5 rounded-lg border border-slate-200/80 bg-white/90 p-2 text-[11px] text-slate-700 sm:grid-cols-3">
                                <div>
                                  <p className="font-semibold uppercase tracking-wide text-slate-500">
                                    {t('account.unit_price', { defaultValue: 'Unit price' })}
                                  </p>
                                  <p className="font-semibold text-slate-900">{formatPrice(unitPrice)}</p>
                                </div>
                                <div>
                                  <p className="font-semibold uppercase tracking-wide text-slate-500">
                                    {t('account.list_price', { defaultValue: 'List price' })}
                                  </p>
                                  <p className="font-semibold text-slate-900">{formatPrice(listPrice)}</p>
                                </div>
                                <div>
                                  <p className="font-semibold uppercase tracking-wide text-slate-500">
                                    {t('account.line_discount', {
                                      defaultValue: 'Line discount',
                                    })}
                                  </p>
                                  <p className="font-semibold text-emerald-700">
                                    {discountPercent.toFixed(0)}% · {formatPrice(lineDiscount)}
                                  </p>
                                </div>
                              </div>
                            ) : null}
                            {isPackage && details.length > 0 ? (
                              <div className="mt-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedPackageRows((prev) => ({
                                      ...prev,
                                      [expandKey]: !prev[expandKey],
                                    }))
                                  }
                                  className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
                                >
                                  {expanded
                                    ? t('account.hide_package_details', {
                                        defaultValue: 'Hide package details',
                                      })
                                    : t('account.view_package_details', {
                                        defaultValue: 'View package details',
                                      })}
                                </button>
                                {expanded ? (
                                  <ul className="mt-2 space-y-1.5">
                                    {details.map((d, idx) => (
                                      <li key={`${expandKey}:${idx}`} className="rounded-md bg-white px-2.5 py-1.5 ring-1 ring-slate-200">
                                        <div className="flex items-center justify-between gap-2">
                                          <span className="text-xs font-bold text-slate-900">{d.name}</span>
                                          <span className="text-[11px] font-semibold text-slate-700">
                                            {t('account.qty_lbs', {
                                              defaultValue: '{{qty}} LB',
                                              qty: d.qtyLbs.toFixed(2),
                                            })}
                                          </span>
                                        </div>
                                        <p className="mt-0.5 text-[11px] text-slate-600">
                                          {t('account.qty_per_bundle_total_lbs', {
                                            defaultValue:
                                              '{{bundle}} LB per bundle · {{total}} LB total',
                                            bundle: d.qtyPerBundleLbs.toFixed(2),
                                            total: d.qtyLbs.toFixed(2),
                                          })}
                                        </p>
                                      </li>
                                    ))}
                                  </ul>
                                ) : null}
                              </div>
                            ) : isPackage ? (
                              <p className="mt-2 text-[11px] font-medium text-slate-500">
                                {t('account.package_details_unavailable', {
                                  defaultValue:
                                    'Package composition details are unavailable for this line.',
                                })}
                              </p>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('account.order_history')}
                    </div>
                    <ul className="mt-1 space-y-1 text-xs text-slate-700">
                      {o.events.map((ev) => (
                        <li key={ev.id}>
                          {ev.fromStatus
                            ? t('account.order_event_change', {
                                from: formatStatus(ev.fromStatus),
                                to: formatStatus(ev.toStatus ?? ''),
                              })
                            : ev.toStatus
                              ? formatStatus(ev.toStatus)
                              : t('account.order_event_update')}
                          {' · '}
                          <span className="text-slate-500">{new Date(ev.createdAt).toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {['PENDING', 'PREORDER_AWAITING_RELEASE', 'PREORDER_BALANCE_DUE'].includes(o.status) ? (
                    <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => void cancelOrder(o.id, Boolean(o.isPreorderOrder))}
                        className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-rose-700 hover:border-rose-300"
                        disabled={busyOrderId === o.id}
                      >
                        {busyOrderId === o.id
                          ? t('common.loading')
                          : t('account.cancel_order')}
                      </button>
                    </div>
                  ) : null}
                  {['PAID', 'DELIVERED', 'SHIPPED', 'RESHIPPED'].includes(o.status) &&
                  !o.refundRequests?.length ? (
                    <div className="mt-2 flex items-center justify-end">
                      {(() => {
                        const isShipping =
                          o.status === 'SHIPPED' || o.status === 'RESHIPPED';
                        const label = isShipping
                          ? t('account.refund_available_after_delivery', {
                              defaultValue: 'Refund available after delivery',
                            })
                          : o.status === 'DELIVERED'
                            ? t('account.request_delivered_refund', {
                                defaultValue: 'Request delivered refund',
                              })
                            : t('account.request_paid_refund', {
                                defaultValue: 'Request paid refund',
                              });
                        const title = isShipping
                          ? 'Refund request is disabled while order is shipping. Available again after delivery.'
                          : undefined;
                        return (
                      <button
                        type="button"
                        disabled={isShipping}
                        onClick={() =>
                          setRefundModal({
                            orderId: o.id,
                            stage: o.status === 'DELIVERED' ? 'DELIVERED' : 'PAID',
                            reason:
                              o.status === 'DELIVERED'
                                ? 'WRONG_ITEM_SENT'
                                : 'CHANGE_OF_MIND',
                            note: '',
                            evidenceUrl: '',
                            evidenceExplanation: '',
                          })
                        }
                        className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800 hover:border-amber-400 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                        title={title}
                      >
                        {label}
                      </button>
                        );
                      })()}
                    </div>
                  ) : null}
                    </>
                  ) : null}
                </div>
                );
              })}
              <div className="flex flex-col gap-2 border-t border-slate-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-500">
                  {t('account.orders_pagination_info', {
                    page: safePage,
                    totalPages,
                  })}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('common.prev')}
                  </button>
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            </div>
              );
            })()
          )}
        </section>
      </div>
      {balancePayModal ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/40 p-3 sm:p-4">
          <div className="max-h-[92dvh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {t('account.pay_remaining_balance_title', {
                    defaultValue: 'Pay remaining pre-order balance',
                  })}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  {t('account.pay_remaining_balance_hint', {
                    defaultValue:
                      'Complete the final card payment to move this order to paid and continue fulfillment.',
                  })}
                </p>
              </div>
              <button
                type="button"
                className="rounded-full border border-slate-300 p-1 text-slate-500 hover:bg-slate-100"
                onClick={() => setBalancePayModal(null)}
                aria-label={t('common.close', { defaultValue: 'Close' })}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {stripePromise ? (
              <div className="mt-4">
                <Elements stripe={stripePromise} options={{ clientSecret: balancePayModal.clientSecret }}>
                  <CheckoutPaymentForm
                    onSuccess={handleBalancePaymentSuccess}
                    totalDueFormatted={balancePayModal.dueFormatted}
                  />
                </Elements>
              </div>
            ) : (
              <p className="mt-3 text-sm text-rose-700">
                {t('storefront.checkout_stripe_no_pk', {
                  defaultValue: 'Card payments are currently unavailable. Please try again later.',
                })}
              </p>
            )}
          </div>
        </div>
      ) : null}
      {refundModal ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/40 p-3 sm:p-4">
          <div className="max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl sm:p-5">
            <h3 className="text-base font-semibold text-slate-900">
              {refundModal.stage === 'PAID'
                ? t('account.request_refund_paid_order', {
                    defaultValue: 'Request refund (Paid order)',
                  })
                : t('account.request_refund_delivered_order', {
                    defaultValue: 'Request refund (Delivered order)',
                  })}
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              {refundModal.stage === 'PAID'
                ? t('account.refund_paid_stage_hint', {
                    defaultValue: 'Paid-stage refund is quick: select a reason and submit.',
                  })
                : t('account.refund_delivered_stage_hint', {
                    defaultValue:
                      'Delivered-order refund requires evidence so our team can validate the issue.',
                  })}
            </p>
            <div className="mt-3 grid gap-3">
              <label className="text-sm text-slate-700">
                {t('account.refund_reason_label', { defaultValue: 'Reason' })}
                <select
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={refundModal.reason}
                  onChange={(e) =>
                    setRefundModal((prev) =>
                      prev ? { ...prev, reason: e.target.value } : prev,
                    )
                  }
                >
                  {refundModal.stage === 'PAID' ? (
                    <>
                      <option value="CHANGE_OF_MIND">
                        {t('account.refund_reason_change_of_mind', {
                          defaultValue: 'Changed my mind',
                        })}
                      </option>
                      <option value="OTHER">{t('common.other', { defaultValue: 'Other' })}</option>
                    </>
                  ) : (
                    <>
                      <option value="WRONG_ITEM_SENT">
                        {t('account.refund_reason_wrong_item', {
                          defaultValue: 'Wrong item sent',
                        })}
                      </option>
                      <option value="PACKAGE_DAMAGED">
                        {t('account.refund_reason_package_damaged', {
                          defaultValue: 'Packaging damaged / seal broken',
                        })}
                      </option>
                      <option value="EXPIRED_OR_SPOILED">
                        {t('account.refund_reason_expired_spoiled', {
                          defaultValue: 'Expired / mold / spoilage',
                        })}
                      </option>
                      <option value="QUALITY_ISSUE">
                        {t('account.refund_reason_quality_issue', {
                          defaultValue: 'Quality issue (stale/contaminated)',
                        })}
                      </option>
                    </>
                  )}
                </select>
              </label>
              <label className="text-sm text-slate-700">
                {t('account.refund_note_optional', {
                  defaultValue: 'Note (optional)',
                })}
                <textarea
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  rows={3}
                  value={refundModal.note}
                  onChange={(e) =>
                    setRefundModal((prev) =>
                      prev ? { ...prev, note: e.target.value } : prev,
                    )
                  }
                />
              </label>
              {refundModal.stage === 'DELIVERED' ? (
                <label className="text-sm text-slate-700">
                  {t('account.refund_evidence_required_label', {
                    defaultValue: 'Evidence photo (required)',
                  })}
                  <input
                    type="file"
                    accept="image/*"
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const fd = new FormData();
                      fd.append('file', f);
                      const res = await authorizedFetch(
                        `${API_BASE}/sales/refund-evidence/image`,
                        { method: 'POST', body: fd },
                      );
                      if (!res.ok) return;
                      const data = (await res.json()) as { url?: string | null };
                      if (data?.url) {
                        setRefundModal((prev) =>
                          prev ? { ...prev, evidenceUrl: data.url || '' } : prev,
                        );
                      }
                    }}
                  />
                  {refundModal.evidenceUrl ? (
                    <a
                      href={refundModal.evidenceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 block text-xs text-brand-700 underline"
                    >
                      {t('account.refund_uploaded_evidence', {
                        defaultValue: 'Uploaded evidence',
                      })}
                    </a>
                  ) : (
                    <p className="mt-1 text-xs text-rose-600">
                      {t('account.refund_upload_evidence_before_submit', {
                        defaultValue: 'Please upload an evidence image before submitting.',
                      })}
                    </p>
                  )}
                </label>
              ) : null}
            </div>
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:w-auto"
                onClick={() => setRefundModal(null)}
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                type="button"
                className="w-full rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white sm:w-auto"
                onClick={() => void requestRefund()}
              >
                {t('account.submit_refund_request', {
                  defaultValue: 'Submit request',
                })}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </CustomerLayout>
  );
}

